/**
 * Integrytas Group — Theme selector (header).
 *
 * A swatch button placed beside the identity chip. Opens a popover listing every
 * available theme (built-in + org + personal) with a live preview on hover; click
 * to apply (persists per-user). "New theme" / edit opens the <ThemeDesigner>.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Palette, Plus, Pencil, Trash2, Check, Star } from 'lucide-react';
import { usePresentation } from '../../lib/use_presentation';
import type { ThemeDefinition } from '../../lib/theme/theme_tokens';
import { ThemeDesigner } from './ThemeDesigner';

function Swatch({ theme }: { theme: ThemeDefinition }) {
  const dot = (hex: string) => (
    <span className="h-3.5 w-3.5 rounded-sm border border-black/10" style={{ backgroundColor: hex }} />
  );
  return (
    <span className="flex items-center gap-0.5">
      {dot(theme.colors['sidebar-bg'])}
      {dot(theme.colors['primary'])}
      {dot(theme.colors['accent'])}
      {dot(theme.colors['surface'])}
    </span>
  );
}

export function ThemeSelector() {
  const { t } = useTranslation();
  const pres = usePresentation();
  const [open, setOpen] = useState(false);
  const [designer, setDesigner] = useState<{ initial: ThemeDefinition; mode: 'new' | 'edit' } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) { setOpen(false); pres.preview(null); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, pres]);

  const scopeLabel = (s: ThemeDefinition['scope']) =>
    s === 'user' ? t('theme.scope_personal', 'Personal') : s === 'org' ? t('theme.scope_org', 'Org') : t('theme.scope_builtin', 'Built-in');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('theme.selector_label', 'Theme')}
        title={t('theme.selector_label', 'Theme')}
        className="flex h-7 w-7 items-center justify-center rounded text-white/80 hover:bg-white/10 hover:text-white"
      >
        <Palette className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[200] mt-1 w-72 rounded border border-csm-border bg-csm-surface shadow-xl">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">{t('theme.panel_title', 'Theme')}</span>
            <button
              type="button"
              onClick={() => { setDesigner({ initial: pres.activeTheme, mode: 'new' }); setOpen(false); pres.preview(null); }}
              className="flex items-center gap-1 rounded border border-csm-border bg-csm-surface px-2 py-0.5 text-csm-xs text-csm-ink-soft hover:bg-csm-surface-alt"
            >
              <Plus className="h-3 w-3" /> {t('theme.new', 'New')}
            </button>
          </div>
          <div className="max-h-80 overflow-auto border-t border-csm-border-soft py-1">
            {pres.themes.map((th) => {
              const active = th.code === pres.themeCode;
              return (
                <div
                  key={th.code}
                  onMouseEnter={() => pres.preview(th)}
                  onMouseLeave={() => pres.preview(null)}
                  className={`group flex items-center gap-2 px-3 py-1.5 text-csm-sm ${active ? 'bg-primary/5' : 'hover:bg-csm-surface-alt'}`}
                >
                  <button type="button" onClick={() => { pres.setThemeCode(th.code); pres.preview(null); setOpen(false); }} className="flex flex-1 items-center gap-2 text-left">
                    <span className="flex h-4 w-4 items-center justify-center text-primary">{active && <Check className="h-3.5 w-3.5" />}</span>
                    <Swatch theme={th} />
                    <span className="flex-1 truncate text-csm-ink">{th.name}</span>
                    <span className="rounded-sm bg-csm-surface-alt px-1 text-csm-2xs uppercase tracking-wide text-csm-muted">{scopeLabel(th.scope)}</span>
                  </button>
                  {th.scope === 'user' && (
                    <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                      <button type="button" aria-label={t('common.edit', 'Edit')} title={t('common.edit', 'Edit')} onClick={() => { setDesigner({ initial: th, mode: 'edit' }); setOpen(false); pres.preview(null); }} className="rounded p-0.5 text-csm-muted hover:text-csm-ink"><Pencil className="h-3.5 w-3.5" /></button>
                      <button type="button" aria-label={t('common.delete', 'Delete')} title={t('common.delete', 'Delete')} onClick={() => { void pres.deleteUserTheme(th); }} className="rounded p-0.5 text-csm-muted hover:text-csm-danger"><Trash2 className="h-3.5 w-3.5" /></button>
                    </span>
                  )}
                  {pres.isAdmin && th.scope === 'org' && (
                    <button type="button" aria-label={t('common.edit', 'Edit')} title={t('common.edit', 'Edit')} onClick={() => { setDesigner({ initial: th, mode: 'edit' }); setOpen(false); pres.preview(null); }} className="rounded p-0.5 text-csm-muted opacity-0 hover:text-csm-ink group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              );
            })}
          </div>
          {pres.isAdmin && (
            <div className="flex items-center gap-1.5 border-t border-csm-border-soft px-3 py-2 text-csm-xs text-csm-ink-soft">
              <Star className="h-3 w-3 text-csm-warning" />
              <span>{t('theme.app_default_hint', 'In the designer you can publish org themes and set the app default.')}</span>
            </div>
          )}
        </div>
      )}

      {designer && (
        <ThemeDesigner initial={designer.initial} mode={designer.mode} onClose={() => setDesigner(null)} />
      )}
    </div>
  );
}
